import ImageQualityStatus from '../../../enums/ImageQualityStatus.js';
import { PANEL_CONFIG, PANEL_CONFIGS } from './constants.js';
import { PanelType } from './enums.js';
export class RenderModesPanel {
    constructor() {
        const config = PANEL_CONFIGS[PanelType.RENDER_MODES];
        this.dom = document.createElement('div');
        this.dom.style.cssText = `
      width:min(622px, max(${PANEL_CONFIG.WIDE_WIDTH}px, calc(100vw - ${PANEL_CONFIG.WIDTH + 24}px)));
      min-width:0;
      min-height:${PANEL_CONFIG.HEIGHT}px;
      background:${config.backgroundColor};
      color:${config.foregroundColor};
      font:${PANEL_CONFIG.FONT_SIZE + 2}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      padding:8px;
      box-sizing:border-box;
      max-height:min(520px, calc(100vh - 16px));
      overflow:auto;
      border:1px solid rgba(255, 204, 0, 0.35);
      border-radius:4px;
      box-shadow:0 8px 28px rgba(0, 0, 0, 0.35);
    `;
        const title = document.createElement('div');
        title.textContent = config.name;
        title.style.cssText = `
      margin-bottom:8px;
      font-weight:700;
      letter-spacing:0.08em;
      text-transform:uppercase;
    `;
        this.dom.appendChild(title);
        this.list = document.createElement('div');
        this.list.style.cssText = `
      display:flex;
      flex-direction:column;
      gap:8px;
      line-height:1.35;
      font-weight:normal;
      color:#fff3bf;
    `;
        this.dom.appendChild(this.list);
    }
    update() {
    }
    setContent(entries) {
        this.list.replaceChildren();
        if (!entries.length) {
            this.list.appendChild(createEmptyState('(no viewports)'));
            return;
        }
        for (const entry of entries) {
            const bindings = orderBindings(entry.bindings);
            const volumes = entry.volumes ?? [];
            const section = document.createElement('div');
            section.style.cssText = `
        padding:7px;
        background:rgba(0, 0, 0, 0.18);
        border:1px solid rgba(255, 204, 0, 0.18);
        border-radius:4px;
      `;
            const viewportId = `${entry.renderingEngineId}/${entry.viewportId}`;
            const heading = document.createElement('div');
            heading.title = viewportId;
            heading.style.cssText = `
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
        margin-bottom:6px;
        color:#ffd84d;
        font-weight:700;
      `;
            const left = document.createElement('div');
            left.style.cssText = `
        display:flex;
        align-items:center;
        gap:6px;
        min-width:0;
      `;
            const name = document.createElement('span');
            name.textContent = truncate(viewportId, 72);
            name.style.cssText = `
        min-width:0;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
      `;
            left.appendChild(name);
            if (entry.viewportType) {
                const type = document.createElement('span');
                type.textContent = entry.viewportType;
                type.title = entry.viewportType;
                type.style.cssText = `
          flex:0 0 auto;
          padding:2px 6px;
          background:rgba(255, 204, 0, 0.14);
          border:1px solid rgba(255, 204, 0, 0.4);
          border-radius:3px;
          color:#ffe08a;
          font-size:${PANEL_CONFIG.FONT_SIZE}px;
          font-weight:700;
          letter-spacing:0.03em;
        `;
                left.appendChild(type);
            }
            heading.appendChild(left);
            const count = document.createElement('span');
            count.textContent = `${bindings.length} binding${bindings.length === 1 ? '' : 's'} · ${volumes.length} vol${volumes.length === 1 ? '' : 's'}`;
            count.style.cssText = `
        flex:0 0 auto;
        color:#c9b05a;
        font-size:${PANEL_CONFIG.FONT_SIZE}px;
        font-weight:600;
      `;
            heading.appendChild(count);
            section.appendChild(heading);
            if (!bindings.length && !volumes.length) {
                section.appendChild(createEmptyState('(no bindings)'));
                this.list.appendChild(section);
                continue;
            }
            for (const binding of bindings) {
                section.appendChild(createBindingRow(binding));
            }
            for (const volume of volumes) {
                section.appendChild(createVoxelQualityRow(volume));
            }
            this.list.appendChild(section);
        }
    }
}
function orderBindings(bindings) {
    const rank = {
        source: 0,
        overlay: 1,
        data: 2,
    };
    return [...bindings].sort((a, b) => {
        const roleDelta = rank[a.role] - rank[b.role];
        if (roleDelta !== 0) {
            return roleDelta;
        }
        return a.dataId.localeCompare(b.dataId);
    });
}
function truncate(value, max) {
    if (value.length <= max) {
        return value;
    }
    const head = Math.ceil((max - 1) / 2);
    const tail = Math.floor((max - 1) / 2);
    return `${value.slice(0, head)}...${value.slice(-tail)}`;
}
const ROLE_STYLES = {
    source: {
        background: 'rgba(0, 104, 126, 0.34)',
        border: 'rgba(92, 225, 255, 0.4)',
        color: '#9decff',
        label: 'SOURCE',
    },
    overlay: {
        background: 'rgba(165, 102, 0, 0.3)',
        border: 'rgba(255, 205, 83, 0.45)',
        color: '#ffd76d',
        label: 'OVERLAY',
    },
    data: {
        background: 'rgba(96, 101, 116, 0.28)',
        border: 'rgba(187, 195, 214, 0.32)',
        color: '#dce5ff',
        label: 'DATA',
    },
};
function createBindingRow(binding) {
    const row = document.createElement('div');
    row.style.cssText = `
    display:grid;
    grid-template-columns:74px minmax(0, 1fr);
    gap:8px;
    padding:6px;
    margin-top:4px;
    background:rgba(255, 255, 255, 0.045);
    border-radius:4px;
  `;
    const roleStyle = ROLE_STYLES[binding.role];
    const role = document.createElement('div');
    role.textContent = roleStyle.label;
    role.style.cssText = `
    align-self:start;
    padding:3px 5px;
    background:${roleStyle.background};
    border:1px solid ${roleStyle.border};
    border-radius:3px;
    color:${roleStyle.color};
    font-size:${PANEL_CONFIG.FONT_SIZE}px;
    font-weight:800;
    letter-spacing:0.04em;
    text-align:center;
  `;
    row.appendChild(role);
    const fields = document.createElement('div');
    fields.style.cssText = `
    display:flex;
    flex-direction:column;
    gap:3px;
    min-width:0;
  `;
    appendField(fields, 'mode', binding.renderMode, 48);
    appendField(fields, 'data', binding.dataId, 76);
    if (binding.actorUID && binding.actorUID !== binding.dataId) {
        appendField(fields, 'actor', binding.actorUID, 76);
    }
    if (binding.referencedId &&
        binding.referencedId !== binding.dataId &&
        binding.referencedId !== binding.actorUID) {
        appendField(fields, 'reference', binding.referencedId, 76);
    }
    if (binding.scalarType) {
        appendField(fields, 'type', formatScalarType(binding.scalarType, binding.numberOfComponents), 40);
    }
    if (binding.scalarRange) {
        appendField(fields, 'range', formatScalarRange(binding.scalarRange), 56);
    }
    row.appendChild(fields);
    return row;
}
function createVoxelQualityRow(volume) {
    const row = document.createElement('div');
    row.style.cssText = `
    display:grid;
    grid-template-columns:74px minmax(0, 1fr);
    gap:8px;
    padding:6px;
    margin-top:4px;
    background:rgba(153, 204, 255, 0.06);
    border:1px solid rgba(153, 204, 255, 0.18);
    border-radius:4px;
  `;
    const badge = document.createElement('div');
    badge.textContent = 'QUALITY';
    badge.style.cssText = `
    align-self:start;
    padding:3px 5px;
    background:rgba(0, 104, 126, 0.28);
    border:1px solid rgba(153, 204, 255, 0.4);
    border-radius:3px;
    color:#9cf;
    font-size:${PANEL_CONFIG.FONT_SIZE}px;
    font-weight:800;
    letter-spacing:0.04em;
    text-align:center;
  `;
    row.appendChild(badge);
    const fields = document.createElement('div');
    fields.style.cssText = `
    display:flex;
    flex-direction:column;
    gap:3px;
    min-width:0;
  `;
    const { record } = volume;
    if (!record) {
        fields.appendChild(createEmptyState('(no VoxelQualityRecord)'));
        row.appendChild(fields);
        return row;
    }
    appendField(fields, 'reduction', String(record.reduction), 40);
    appendField(fields, 'source', String(record.source), 40);
    appendField(fields, 'status', formatQualityRange(record.status, record.lowest, record.highest), 64);
    appendField(fields, 'missing', formatMissing(record.missing, record.voxels, record.exact), 48);
    appendField(fields, 'grid', formatSpacing(record.grid?.spacing), 48);
    appendField(fields, 'deliveries', String(record.deliveries ?? 0), 24);
    row.appendChild(fields);
    return row;
}
function formatQualityRange(status, lowest, highest) {
    const summary = formatQualityStatus(status);
    if (lowest === undefined ||
        highest === undefined ||
        (lowest === status && highest === status)) {
        return summary;
    }
    return `${summary} [${formatQualityStatus(lowest)}..${formatQualityStatus(highest)}]`;
}
function formatQualityStatus(status) {
    if (status === undefined) {
        return '-';
    }
    return ImageQualityStatus[status] ?? String(status);
}
function formatMissing(missing, voxels, exact) {
    if (!(voxels > 0)) {
        return '0/0';
    }
    const pct = Math.round((100 * missing) / voxels);
    const approx = exact ? '' : '~';
    return `${approx}${missing}/${voxels} (${approx}${pct}%)`;
}
function formatSpacing(spacing) {
    if (!spacing?.length) {
        return '-';
    }
    return spacing
        .map((value) => {
        if (!Number.isFinite(value)) {
            return '?';
        }
        if (Number.isInteger(value)) {
            return String(value);
        }
        return Number(value.toFixed(3)).toString();
    })
        .join('×');
}
function formatScalarType(scalarType, numberOfComponents) {
    const friendly = scalarType.replace(/Array$/, '');
    return numberOfComponents && numberOfComponents > 1
        ? `${friendly} x${numberOfComponents}`
        : friendly;
}
function formatScalarRange([min, max]) {
    return `${formatScalarValue(min)} .. ${formatScalarValue(max)}`;
}
function formatScalarValue(value) {
    if (Number.isInteger(value)) {
        return String(value);
    }
    return Number(value.toFixed(2)).toString();
}
function appendField(parent, label, value, maxLength) {
    const field = document.createElement('div');
    field.style.cssText = `
    display:grid;
    grid-template-columns:88px minmax(0, 1fr);
    gap:6px;
    min-width:0;
  `;
    const labelNode = document.createElement('span');
    labelNode.textContent = label;
    labelNode.style.cssText = `
    color:#a9954c;
    font-size:${PANEL_CONFIG.FONT_SIZE}px;
    font-weight:700;
    text-transform:uppercase;
  `;
    field.appendChild(labelNode);
    const valueNode = document.createElement('span');
    valueNode.textContent = truncate(value, maxLength);
    valueNode.title = value;
    valueNode.style.cssText = `
    min-width:0;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
    color:#fff7d6;
  `;
    field.appendChild(valueNode);
    parent.appendChild(field);
}
function createEmptyState(message) {
    const empty = document.createElement('div');
    empty.textContent = message;
    empty.style.cssText = `
    color:#b7a865;
    font-style:italic;
  `;
    return empty;
}
